import { Linkedin, Mail, ArrowRight, BadgeCheck, CalendarCheck, MessageSquare } from "lucide-react";
import { CONTACT, whatsappHref } from "../../config/contact";

const cols = [
  {
    title: "Services",
    items: [
      "Digital Publishing",
      "Content Management",
      "Typesetting",
      "Graphics Studio",
      "Accessibility",
      "Digital Conversion",
      "Interactive Learning",
    ],
  },
  {
    title: "Industries",
    items: ["Publishing", "Education", "Museums", "Government", "Healthcare", "Legal", "Technology"],
  },
  {
    title: "Company",
    items: [
      { label: "About", href: "#about" },
      { label: "Workflow", href: "#workflow" },
      { label: "Clientele", href: "#clientele" },
      { label: "Contact", href: "#contact" },
      { label: "Careers (Coming Soon)", href: "#" },
    ],
  },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-[#0B1F3A] text-white overflow-hidden">
      <div className="absolute -top-24 left-1/4 h-[300px] w-[300px] rounded-full bg-[#1F5EFF]/20 blur-[100px]" />
      <div className="absolute -bottom-32 right-0 h-[320px] w-[320px] rounded-full bg-[#6D28D9]/20 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2.5">
              <img
                src="https://customer-assets.emergentagent.com/job_accessibility-first-5/artifacts/4w169tlv_ChatGPT%20Image%20Jan%201%2C%202026%2C%2002_13_50%20AM.png"
                alt=""
                className="h-11 w-11 rounded-full object-cover bg-white"
              />
              <span className="font-display text-[18px] font-semibold tracking-tight">Libros Consulting</span>
            </div>
            <p className="mt-5 text-[14px] text-white/70 leading-relaxed max-w-sm">
              Where Content Meets Technology. End-to-end publishing, accessibility, and
              content technology for organizations worldwide.
            </p>

            <div className="mt-6">
              <div className="overline text-[#00B8A9]">Newsletter</div>
              <form
                data-testid="newsletter-form"
                aria-label="Newsletter subscription"
                onSubmit={(e) => e.preventDefault()}
                className="mt-2 flex items-center gap-2 rounded-full bg-white/5 border border-white/10 p-1.5 max-w-md"
              >
                <label htmlFor="newsletter-email" className="sr-only">Email address</label>
                <input
                  data-testid="newsletter-input"
                  id="newsletter-email"
                  type="email"
                  placeholder="your@email.com"
                  className="flex-1 bg-transparent px-4 py-2 text-[14px] placeholder:text-white/40 outline-none"
                />
                <button
                  data-testid="newsletter-submit"
                  type="submit"
                  className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#0B1F3A] px-4 py-2 text-[13px] font-semibold hover:bg-[#00B8A9] hover:text-white transition-colors"
                >
                  Subscribe <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </form>
            </div>
          </div>

          {cols.map((c) => (
            <div key={c.title} className="lg:col-span-2">
              <div className="overline text-white/60">{c.title}</div>
              <ul className="mt-4 space-y-2.5">
                {c.items.map((it) => {
                  const label = typeof it === "string" ? it : it.label;
                  const href = typeof it === "string" ? "#services" : it.href;
                  return (
                    <li key={label}>
                      <a href={href} className="inline-block py-1 text-[14px] text-white/80 hover:text-white transition-colors">
                        {label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <div className="overline text-white/60">Contact</div>
            <ul className="mt-4 space-y-2.5 text-[14px] text-white/80">
              <li>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/60">Sales</div>
                <a href={`mailto:${CONTACT.salesEmail}`} className="inline-flex items-center gap-2 py-0.5 hover:text-white">
                  <Mail className="h-4 w-4 text-[#00B8A9]" aria-hidden="true" />
                  {CONTACT.salesEmail}
                </a>
              </li>
              <li>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/60">General</div>
                <a href={`mailto:${CONTACT.generalEmail}`} className="inline-flex items-center gap-2 py-0.5 hover:text-white">
                  <Mail className="h-4 w-4 text-[#00B8A9]" aria-hidden="true" />
                  {CONTACT.generalEmail}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-schedule-link"
                  href={CONTACT.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Schedule a Consultation (opens in new tab)"
                  className="inline-flex items-center gap-2 py-0.5 hover:text-white"
                >
                  <CalendarCheck className="h-4 w-4 text-[#00B8A9]" aria-hidden="true" />
                  Schedule a Consultation
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-whatsapp-link"
                  href={whatsappHref()}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Chat on WhatsApp (opens in new tab)"
                  className="inline-flex items-center gap-2 py-0.5 hover:text-white"
                >
                  <MessageSquare className="h-4 w-4 text-[#00B8A9]" aria-hidden="true" />
                  Chat on WhatsApp
                </a>
              </li>
              <li className="text-white/70">Serving global clients from India</li>
            </ul>
            <div className="mt-5 flex items-center gap-2">
              <a
                data-testid="social-linkedin"
                href="https://www.linkedin.com/in/libros-consulting-142068328/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Libros Consulting on LinkedIn (opens in new tab)"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
              >
                <Linkedin className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="text-[13px] text-white/60">
              © {new Date().getFullYear()} Libros Consulting. All rights reserved.
            </div>
            <span
              data-testid="wcag-badge"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#00B8A9]/40 bg-[#00B8A9]/10 px-3 py-1 text-[11px] font-semibold text-[#00B8A9]"
            >
              <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Built with WCAG 2.2 AA best practices
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-white/60">
            <a href="#" className="inline-block py-1 hover:text-white">Privacy Policy</a>
            <a href="#" className="inline-block py-1 hover:text-white">Terms</a>
            <a href="/accessibility-statement" className="inline-block py-1 hover:text-white">Accessibility Statement</a>
            <a href="#" className="inline-block py-1 hover:text-white">Cookie Policy</a>
            <a href="/sitemap.xml" className="inline-block py-1 hover:text-white">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
